import React, { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/custom/button";
import { DialogClose, DialogFooter } from "@/components/ui/dialog";
import SelectInputCreatable from "@/components/select-input/select-input-creatable";
import { GroupBase } from "react-select";
import {
  markRemovable,
  OptionType,
} from "@/components/select-input/select-input";

type SelectTagOption = {
  __isNew__?: boolean;
  label: string;
  value: string | number;
  partial?: boolean;
  className?: string;
  tag?: { id: number; name: string };
};

interface TagGroupFormValues {
  tags: SelectTagOption[];
}

interface ManageTagsComponentProps {
  form: UseFormReturn<Partial<TagGroupFormValues>>;
  onSubmit: (data: Partial<TagGroupFormValues>) => Promise<void>;
  tagGroups: GroupBase<OptionType | SelectTagOption>[];
  /**
   * The user's role may unassign locked tags (`<entity>.tags.unassignLocked`). Otherwise the
   * locked tags already on the selection can't be removed here.
   */
  canRemoveLocked?: boolean;
  isSubmitting?: boolean;
  descriptionKey?: string;
  tagsListKey?: string;
  cancelKey?: string;
  updateKey?: string;
  updatingKey?: string;
}

/**
 * ManageTagsComponent - A reusable component for managing tags
 *
 * This component provides a standardized UI for tag management operations including:
 * - Tag selection/deselection with multi-select support
 * - New tag creation
 * - Form validation
 * - Submit/Cancel actions
 *
 * @param form - React Hook Form instance for managing form state
 * @param onSubmit - Callback function called when form is submitted
 * @param tagGroups - Array of tag group options for the select input
 * @param isSubmitting - Whether the form is currently being submitted
 * @param descriptionKey - Translation key for the description text (default: "deviceTagsDescription")
 * @param tagsListKey - Translation key for the tags field label (default: "tagsList")
 */
export const ManageTagsComponent: React.FC<ManageTagsComponentProps> = ({
  form,
  onSubmit,
  tagGroups,
  canRemoveLocked = false,
  isSubmitting = false,
  descriptionKey = "addingEditingTagsDescription",
  tagsListKey = "tagsList",
}) => {
  const { t } = useTranslation();
  // keepLocked (below) holds on to locked tags (ID-7095), but only the ones already on the
  // selection, and only for a role that can't unassign locked tags: one picked from the menu
  // isn't assigned yet.
  const menuGroups = useMemo(
    () =>
      tagGroups.map((group) => ({
        ...group,
        options: group.options.map(markRemovable),
      })),
    [tagGroups],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-2">{t(descriptionKey)}</p>
        </div>

        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem className="py-4">
              <FormLabel>{t(tagsListKey)}</FormLabel>
              <FormControl>
                <SelectInputCreatable
                  {...field}
                  value={
                    canRemoveLocked
                      ? field.value?.map(markRemovable)
                      : field.value
                  }
                  isMulti={true}
                  isClearable={true}
                  keepLocked={true}
                  // A tag name may hold spaces: a typed one stays whole, and a paste splits on new
                  // lines only (ID-7121).
                  delimiter={"\n"}
                  options={menuGroups}
                />
              </FormControl>
              <FormMessage>{form.formState.errors.tags?.message}</FormMessage>
            </FormItem>
          )}
        />
        <hr />
        <DialogFooter className="">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("cancel")}
            </Button>
          </DialogClose>
          <Button type="submit" variant="azure" disabled={isSubmitting}>
            {isSubmitting ? t("updating") : t("update")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
